import { useState } from "react";
import { useLang } from "../i18n/LanguageContext";
import { symbolsClient } from "../features/symbols/api/symbolsClient";
import {
  GiMoon,
  GiWaterDrop,
  GiKey,
  GiHouse,
  GiFalconMoon,
  GiPathDistance,
} from "react-icons/gi";

const ICONS = [GiMoon, GiWaterDrop, GiKey, GiHouse, GiFalconMoon, GiPathDistance];

const COPY = {
  pl: {
    eyebrow: "Atlas Symboli",
    title: "Znaki, które wracają w snach",
    subtitle:
      "Morfeusz ma łączyć symbole z Księgą Snów: pokazywać, które obrazy powracają i jak zmienia się ich znaczenie.",
    catcherTitle: "Odczytaj jeden symbol ze snu",
    catcherSubtitle: "Gościnnie: 1 prosty symbol dziennie. W Księdze Snów odczytasz symbole z kontekstem.",
    placeholder: "np. woda, dom, klucz...",
    submit: "Odczytaj symbol",
    loading: "Odczytuję…",
    empty: "Podaj symbol.",
    error: "Nie udało się odczytać symbolu.",
    fullPage: "Pełna Symbolika",
  },
  en: {
    eyebrow: "Symbol Atlas",
    title: "Signs that return in dreams",
    subtitle:
      "Morpheus connects symbols with the Dream Codex: showing which images return and how their meaning changes.",
    catcherTitle: "Read one dream symbol",
    catcherSubtitle: "Guest: 1 simple symbol a day. In the Dream Codex you can read symbols with context.",
    placeholder: "e.g. water, house, key...",
    submit: "Read symbol",
    loading: "Reading…",
    empty: "Enter a symbol.",
    error: "Could not read this symbol.",
    fullPage: "Full Symbolism",
  },
};

export const Symbols = () => {
  const { lang, t } = useLang();
  const copy = COPY[lang] ?? COPY.pl;
  const [term, setTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [result, setResult] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const symbol = term.trim();
    if (!symbol) {
      setStatus(copy.empty);
      return;
    }
    setLoading(true);
    setStatus("");
    setResult(null);
    try {
      const response = await symbolsClient.interpret({ terms: [symbol], context: "", language: lang });
      setResult(response?.results?.[0] || null);
      setStatus("");
    } catch (error) {
      setStatus(error?.message || copy.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="symbols"
      className="parchment-texture py-24 md:py-32"
      data-ui="home-symbols"
      data-testid="symbols-section"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10" data-ui="home-symbols-inner">
        <div className="md:grid md:grid-cols-12 md:gap-12 items-end mb-14">
          <div className="md:col-span-7">
            <span className="font-accent text-[11px] uppercase tracking-[0.3em] text-gold-muted">
              {copy.eyebrow}
            </span>
            <h2 className="font-heading italic text-4xl md:text-5xl text-ink mt-3">
              {copy.title}
            </h2>
          </div>
          <p className="md:col-span-5 font-body text-base text-ink-soft leading-relaxed mt-4 md:mt-0">
            {copy.subtitle}
          </p>
        </div>

        <form className="mdCard" data-ui="home-symbol-catcher" onSubmit={handleSubmit} noValidate>
          <p className="mdPage__eyebrow">{copy.catcherTitle}</p>
          <p className="mdPage__subtitle">{copy.catcherSubtitle}</p>
          <div className="mdField">
            <input
              className="mdField__input"
              type="text"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder={copy.placeholder}
              data-ui="home-symbol-input"
            />
          </div>
          <div className="mdRow" data-ui="home-symbol-actions">
            <button type="submit" className="mdBtn mdBtn--primary" disabled={loading || !term.trim()}>
              {loading ? copy.loading : copy.submit}
            </button>
            <a className="mdBtn" href="#/symbolika">{copy.fullPage}</a>
          </div>
          {status && <div className="mdAlert" data-ui="home-symbol-status">{status}</div>}
          {result && (
            <div className="mdFinder__result" data-ui="home-symbol-result">
              <h3 className="mdFinder__resultName">{result.term}</h3>
              <p className="mdFinder__resultMeaning">{result.meaning}</p>
            </div>
          )}
        </form>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10" data-ui="home-symbol-grid">
          {t.symbols.items.map((s, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <div
                key={i}
                className="bg-parchment p-8 md:p-10 group hover:bg-parchment-dark transition-colors duration-500 relative"
                data-ui="home-symbol-card"
                data-testid={`symbol-card-${i}`}
              >
                <div className="flex items-start justify-between">
                  <div className="font-accent text-[10px] uppercase tracking-[0.35em] text-gold-muted">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <Icon className="text-3xl text-ink-soft/60 group-hover:text-gold-muted transition-colors" />
                </div>
                <h3 className="font-heading italic text-3xl text-ink mt-8">
                  {s.name}
                </h3>
                <p className="font-body text-sm text-ink-soft mt-3 leading-relaxed">
                  {s.meaning}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
